import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaPaperclip, FaTrash, FaUpload } from 'react-icons/fa';
import { BinaryFileRestDataProvider } from '../../../data/BinaryFileRestDataProvider';
import { BinaryFile } from '../../../models/BinaryFile';
import PopupDialog from '../PopupDialog/PopupDialog';
import './AttachmentsPanel.scss';

interface AttachmentsPanelProps {
  parentRecordId: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const AttachmentsPanel: React.FC<AttachmentsPanelProps> = ({ parentRecordId }) => {
  const provider = useMemo(() => new BinaryFileRestDataProvider(), []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [attachments, setAttachments] = useState<BinaryFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BinaryFile | null>(null);

  const loadAttachments = useCallback(async () => {
    if (!parentRecordId) return;
    setLoading(true);
    try {
      const files = await provider.getByParentId(parentRecordId);
      setAttachments(files);
    } finally {
      setLoading(false);
    }
  }, [parentRecordId, provider]);

  useEffect(() => {
    setAttachments([]);
    if (parentRecordId) loadAttachments();
  }, [parentRecordId, loadAttachments]);

  const handleOpenUpload = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadOpen(true);
  };

  const handleCloseUpload = () => {
    setUploadOpen(false);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !parentRecordId) return;
    setUploading(true);
    try {
      await provider.upload(selectedFile, parentRecordId);
      handleCloseUpload();
      await loadAttachments();
    } finally {
      setUploading(false);
    }
  };

  const handleOpen = async (file: BinaryFile) => {
    const blob = await provider.download(file.id);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    await provider.delete(deleteTarget.id);
    setDeleteTarget(null);
    await loadAttachments();
  };

  return (
    <div className="attachments-panel">
      <div className="attachments-panel__header">
        <span className="attachments-panel__header-title">
          <FaPaperclip className="attachments-panel__icon" />
          Attachments
        </span>
        {parentRecordId && (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleOpenUpload}
          >
            <FaUpload style={{ marginRight: 5 }} />
            Upload
          </button>
        )}
      </div>

      <div className="attachments-panel__body">
        {!parentRecordId ? (
          <p className="attachments-panel__hint">Save the record first before adding attachments.</p>
        ) : loading ? (
          <p className="attachments-panel__hint">Loading…</p>
        ) : attachments.length === 0 ? (
          <p className="attachments-panel__hint">No attachments yet.</p>
        ) : (
          <ul className="attachments-panel__list">
            {attachments.map(file => (
              <li key={file.id} className="attachments-panel__item">
                <FaPaperclip className="attachments-panel__item-icon" />
                <button
                  type="button"
                  className="attachments-panel__filename"
                  onClick={() => handleOpen(file)}
                  title="Open / download"
                >
                  {file.fileName}
                </button>
                <span className="attachments-panel__meta">
                  {formatFileSize(file.fileSize)}
                </span>
                <button
                  type="button"
                  className="attachments-panel__delete-btn"
                  aria-label="Delete attachment"
                  title="Delete"
                  onClick={() => setDeleteTarget(file)}
                >
                  <FaTrash />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Upload modal */}
      <PopupDialog
        open={uploadOpen}
        title="Upload Attachment"
        onClose={handleCloseUpload}
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary btn-sm me-2"
              disabled={!selectedFile || uploading}
              onClick={handleUpload}
            >
              <FaUpload style={{ marginRight: 5 }} />
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleCloseUpload}>
              Cancel
            </button>
          </>
        }
      >
        <div className="attachments-panel__upload-body">
          <label className="form-label">Select file</label>
          <input
            ref={fileInputRef}
            type="file"
            className="form-control"
            onChange={handleFileChange}
          />
          {selectedFile && (
            <p className="attachments-panel__selected-info mt-2">
              {selectedFile.name} — {formatFileSize(selectedFile.size)}
            </p>
          )}
        </div>
      </PopupDialog>

      {/* Delete confirmation modal */}
      <PopupDialog
        open={deleteTarget !== null}
        title="Delete Attachment"
        onClose={() => setDeleteTarget(null)}
        actions={
          <>
            <button
              type="button"
              className="btn btn-danger btn-sm me-2"
              onClick={handleDeleteConfirmed}
            >
              <FaTrash style={{ marginRight: 5 }} />
              Delete
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{deleteTarget?.fileName}</strong>?
          <br />
          This action cannot be undone.
        </p>
      </PopupDialog>
    </div>
  );
};

export default AttachmentsPanel;
